import { z } from 'zod';
import { GIFT_CARD_KINDS } from '../constants/gift-cards.js';
import { PRODUCT_TYPES } from '../constants/order-states.js';
import { PRODUCT_SALES_CHANNELS } from '../constants/sales-channels.js';
import { IMPORT_MAX_ROWS_PER_REQUEST } from '../lib/import-chunking.js';
import { importResultSchema, importRowSchema, moneyCentsSchema, patchableSchema, uuidSchema, } from './common.js';
import { SERVICE_PLAN_BILLING_INTERVALS } from './subscription.js';
export const skuSchema = z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/, 'SKU may contain letters, numbers, dot, dash and underscore only');
/** Extra images allowed on a product beyond its main one. */
export const MAX_GALLERY_IMAGES = 10;
/** Device-compatibility tags allowed on one product, and the length of each. */
export const MAX_COMPATIBLE_DEVICES = 20;
export const MAX_DEVICE_TAG_LENGTH = 64;
export const deviceTagSchema = z.string().trim().min(1).max(MAX_DEVICE_TAG_LENGTH);
export const productBaseSchema = z.object({
    sku: skuSchema,
    type: z.enum(PRODUCT_TYPES),
    /** Required when type is 'gift_card' (emailed vs handed over), null for
     *  everything else — also enforced by a database CHECK. */
    giftCardKind: z.enum(GIFT_CARD_KINDS).nullable().default(null),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(4000).nullable().default(null),
    priceCents: moneyCentsSchema,
    costCents: moneyCentsSchema.nullable().default(null),
    /** Optional discounted price, shown crossed-out next to priceCents when
     *  active. Must be less than priceCents — enforced on create here, and on
     *  update in the route (a PATCH may omit either field). */
    salePriceCents: moneyCentsSchema.nullable().default(null),
    category: z.string().trim().max(64).nullable().default(null),
    /** The rate this item is taxed at; null = non-taxable. */
    taxCategoryId: uuidSchema.nullable().default(null),
    brand: z.string().trim().max(64).nullable().default(null),
    /** Free-text device tags ("iPhone 13") this product fits, mainly for
     *  accessories. On update this replaces the whole list — omitted leaves it
     *  untouched, `[]` clears it. */
    compatibleDevices: z.array(deviceTagSchema).max(MAX_COMPATIBLE_DEVICES).default([]),
    imageUrl: z.url().max(500).nullable().default(null),
    /** Extra gallery images in display order; `imageUrl` stays the main one.
     *  On update this replaces the whole list — omitted leaves it untouched,
     *  `[]` clears it. */
    galleryImageUrls: z.array(z.url().max(500)).max(MAX_GALLERY_IMAGES).default([]),
    /** The manufacturer's own SKU/part number — distinct from `sku`. Admin-only:
     *  never included in the storefront, public catalog, or terminal schemas. */
    manufacturerSku: z.string().trim().max(64).nullable().default(null),
    /** Supplier/manufacturer page for viewing or reordering this product.
     *  Admin-only, same as manufacturerSku. */
    sourceUrl: z.url().max(500).nullable().default(null),
    isActive: z.boolean().default(true),
    /** Shop-use only: counted like any stock product, but never offered for
     *  sale on the storefront, public catalog, or a vending machine. A
     *  variation cannot be sellable while its listing is internal — flipped
     *  false there is rejected — and marking a listing internal carries its
     *  variations with it, same shape as `isActive`/deactivation. */
    isInternal: z.boolean().default(false),
    /** Which customer channel offers this product: the website, the kiosk
     *  (tablet, vending machines, cabinets), or both. A variation follows its
     *  listing — changing it on a listing carries its variations along, and
     *  changing it on a variation alone is rejected, same shape as `type`.
     *  Staff-built orders ignore it. */
    salesChannel: z.enum(PRODUCT_SALES_CHANNELS).default('all'),
    /** Once true, the SKU refuses further edits (409) until an admin turns
     *  this back off — see the guard in PATCH /products/:id. Reversible by
     *  any admin with catalog:write; unrelated to isActive/isArchived. */
    skuLocked: z.boolean().default(false),
    /** Free-form per-type attributes (storage size, color, plan minutes…). */
    attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
    /** Only meaningful for type='stock': when to warn about running out. */
    reorderPoint: z.number().int().nonnegative().nullable().default(null),
    /** Set on a variation: the product it is a variant of. Up to three levels
     *  (listing > sub-listing > variation), and the child's type must match the
     *  parent's. Enforced in the database, not just here — see the
     *  products_variant_rules trigger. */
    parentId: uuidSchema.nullable().default(null),
    /** Admin-controlled shop position, low first; ties fall back to name/sku.
     *  On a variation: its position in the listing's picker. */
    sortOrder: z.number().int().min(0).max(9999).default(100),
});
export const createProductSchema = productBaseSchema.superRefine((v, ctx) => {
    if (v.type === 'gift_card' && v.giftCardKind === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['giftCardKind'],
            message: 'Choose whether this gift card is digital or physical',
        });
    }
    if (v.type !== 'gift_card' && v.giftCardKind !== null) {
        ctx.addIssue({
            code: 'custom',
            path: ['giftCardKind'],
            message: 'Only a gift card has a digital or physical kind',
        });
    }
    if (v.type === 'gift_card' && v.salePriceCents !== null) {
        ctx.addIssue({
            code: 'custom',
            path: ['salePriceCents'],
            message: 'A gift card is worth its face value and cannot be put on sale',
        });
    }
    if (v.reorderPoint !== null && v.type !== 'stock') {
        ctx.addIssue({
            code: 'custom',
            path: ['reorderPoint'],
            message: 'Only counted stock has a reorder point',
        });
    }
    if (v.salePriceCents !== null && v.salePriceCents >= v.priceCents) {
        ctx.addIssue({
            code: 'custom',
            path: ['salePriceCents'],
            message: 'Sale price must be less than the regular price',
        });
    }
});
/**
 * Product type can change after creation, but only on a listing or standalone
 * product with no stock on hand and no tracked-unit history — inventory rows
 * depend on it. The route enforces that and moves the variations with it.
 *
 * `patchableSchema`, not `.partial()`: the base carries `.default(null)` on
 * every optional field, and a plain partial hands those defaults to a caller
 * who only sent one field. Deactivating a product then erased its category,
 * brand, description and reorder point along the way.
 *
 * `isArchived` isn't in `productBaseSchema` — a *created* product can't start
 * archived (it would need to also start inactive, which create doesn't ask
 * for), so it's only ever set through an update.
 */
export const updateProductSchema = patchableSchema(productBaseSchema).extend({
    /** Permanently discontinued but kept for its order/inventory history.
     *  Always inactive — the route rejects `{isArchived: true, isActive: true}`
     *  outright, and DB-enforces the invariant either way. Setting this false
     *  only clears the flag; the product stays inactive until reactivated. */
    isArchived: z.boolean().optional(),
});
export const productSchema = productBaseSchema.extend({
    id: uuidSchema,
    isArchived: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** List row: product plus the availability figure that matters for its type. */
export const productListItemSchema = productSchema.extend({
    availableCount: z.number().int().nonnegative(),
});
export const productListRowSchema = productListItemSchema.extend({
    get variations() {
        return z.array(productListRowSchema).default([]);
    },
});
export const productListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    /** A single type, or several comma-separated (e.g. `serialized,sim`) — the
     *  intake and provisioning screens both need "every unit-tracked product",
     *  which is more than one type but not "any type". */
    type: z
        .preprocess((v) => (typeof v === 'string' ? v.split(',') : v), z.array(z.enum(PRODUCT_TYPES)).min(1))
        .optional(),
    category: z.string().trim().max(64).optional(),
    /** Case-insensitive partial match against any compatible-device tag. */
    device: z.string().trim().min(1).max(64).optional(),
    /** Case-insensitive exact match against a compatible-device tag — "iPhone 13"
     *  does not match "iPhone 13 Pro". Used by the Compatibility page. */
    deviceExact: deviceTagSchema.optional(),
    isActive: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    isInternal: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    isArchived: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    /** When true, only top-level listings are counted and paginated, with
     *  their active variations attached inline. Everything else that reads
     *  this endpoint (order-form SKU pickers, label printing, provisioning)
     *  wants every row flat, so this defaults off. */
    grouped: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    /** When true, a listing that has variations is left out: it is never sold
     *  itself, only the variation under it is, so a sale picker has no use for
     *  it. Defaults off for everything that wants the whole catalog. */
    sellable: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    /** When true, only products that can be sold right now: tracked and counted
     *  ones need something on hand, plans, services and gift cards always pass. */
    inStock: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    sort: z
        .enum([
        'name',
        'sku',
        'priceCents',
        'createdAt',
        'type',
        'category',
        'availableCount',
        'sortOrder',
    ])
        .default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const productExportQuerySchema = productListQuerySchema.pick({
    q: true,
    type: true,
    category: true,
    device: true,
    isActive: true,
});
/** Compatibility page: every distinct device tag in use, with how many
 *  products carry it. Case variants collapse into one row. */
export const deviceListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(MAX_DEVICE_TAG_LENGTH).optional(),
});
export const deviceUsageRowSchema = z.object({
    device: z.string(),
    productCount: z.number().int().nonnegative(),
});
/** Add or remove one device tag on one product, atomically. */
export const updateProductDevicesSchema = z.object({
    action: z.enum(['add', 'remove']),
    device: deviceTagSchema,
});
export const productDevicesResponseSchema = z.object({
    id: uuidSchema,
    compatibleDevices: z.array(z.string()),
});
/** Rename a device tag on every product that carries it. A case-only change
 *  ("iphone 13" -> "iPhone 13") is allowed; an identical name is not. */
export const renameDeviceSchema = z
    .object({ from: deviceTagSchema, to: deviceTagSchema })
    .refine((v) => v.from !== v.to, {
    message: 'The new name is the same as the old one',
    path: ['to'],
});
export const renameDeviceResultSchema = z.object({
    updated: z.number().int().nonnegative(),
    productIds: z.array(uuidSchema),
});
/** Bulk edit from the products table: one shared change applied to many
 *  products. Bulk "deactivate" is this with `set: { isActive: false }`, and
 *  bulk "archive" is `set: { isArchived: true }` (which also deactivates —
 *  see the route). Neither ever deletes a row; a true delete is per-product
 *  only, via DELETE /products/:id. */
export const bulkUpdateProductsSchema = z.object({
    productIds: z.array(uuidSchema).min(1).max(200),
    set: updateProductSchema
        .pick({
        priceCents: true,
        category: true,
        isActive: true,
        isArchived: true,
        taxCategoryId: true,
        salesChannel: true,
    })
        .refine((s) => Object.values(s).some((v) => v !== undefined), {
        message: 'Set at least one field',
    }),
});
export const bulkUpdateProductsResultSchema = z.object({
    updated: z.array(uuidSchema),
    failed: z.array(z.object({ id: uuidSchema, reason: z.string() })),
});
/** Copy a listing's image onto everything beneath it (variations and
 *  sub-listings alike). `missing` fills only the ones with no image; `all`
 *  also replaces images that differ. */
export const applyImageToVariationsSchema = z.object({
    mode: z.enum(['all', 'missing']),
});
export const applyImageToVariationsResultSchema = z.object({
    updated: z.array(uuidSchema),
});
export const servicePlanSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    stripePriceId: z.string().max(120).nullable(),
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    monthlyCents: moneyCentsSchema,
});
/**
 * Order-form import needs to answer "is this SKU in the catalog?" for a
 * pasted list, and the answer cannot come from the paginated product list:
 * a catalog past one page would report real SKUs as unknown, and a
 * deactivated product still owns its SKU (the column is unique), so it has
 * to resolve against every product, active or not.
 */
export const resolveSkusSchema = z.object({
    /** As typed on the order form — not `skuSchema`, because the whole point
     *  is to accept tokens that may be malformed or misspelled. */
    skus: z.array(z.string().trim().min(1).max(64)).min(1).max(500),
});
/**
 * Suggests a SKU from a formula (title initials + creation-order numbers +
 * batch date) for the "Generate SKU" button on the product/variant creation
 * forms. Read-only — nothing is reserved, so a caller who abandons the form
 * without submitting leaves no trace.
 */
export const generateSkuQuerySchema = z.object({
    name: z.string().trim().min(1).max(200),
    /** Set when generating for a variation, so the formula can fold in the
     *  parent's initials and both sequence numbers. */
    parentId: uuidSchema.optional(),
});
export const generateSkuResponseSchema = z.object({ sku: skuSchema });
export const skuSuggestionSchema = z.object({
    product: productSchema,
    /** Edit distance between the normalized forms; 0 never appears here
     *  because an exact normalized hit resolves as a match instead. */
    distance: z.number().int().positive(),
});
export const skuResolutionSchema = z.object({
    /** Echoed back exactly as submitted, so the caller can key on it. */
    sku: z.string(),
    /** Set when the SKU names a product outright, ignoring case and
     *  separators — `nok 3310 4g` is the same SKU as `NOK-3310-4G`. */
    match: productSchema.nullable(),
    /** Near misses, closest first, only when there is no match. */
    suggestions: z.array(skuSuggestionSchema),
});
export const resolveSkusResponseSchema = z.object({
    results: z.array(skuResolutionSchema),
});
/**
 * Bulk import from the admin Products screen's CSV dialog. Rows are already
 * header-mapped client-side; matching against the catalog and per-row
 * validation both happen on the server, from the same schemas the single-row
 * create/update endpoints use.
 */
export const importProductsSchema = z.object({
    /** Off by default: a re-imported SKU is reported as a duplicate rather than
     *  silently overwriting what is already priced and live. */
    updateExisting: z.boolean().default(false),
    rows: z.array(importRowSchema).min(1).max(IMPORT_MAX_ROWS_PER_REQUEST),
});
export const importProductsResultSchema = importResultSchema();
// ------------------------------------------------------------- categories
/**
 * The shelves of the shop, as a managed list.
 *
 * A product names its category rather than pointing at an id, so the
 * storefront's `/shop?category=Phones` links stay readable and stable; the
 * cost of that is a rename having to rewrite the products, which the API does
 * in one transaction.
 */
export const productCategorySchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(64),
    /** Shown under the heading on the shop page. Blank shows nothing at all. */
    description: z.string().trim().max(300).nullable(),
    /** Low first. Two categories with the same number fall back to the name. */
    sortOrder: z.number().int().min(0).max(9999),
    isActive: z.boolean(),
    /** When set, only products of this type may be filed here. Null = any type. */
    allowedType: z.enum(PRODUCT_TYPES).nullable(),
    /** How many products are filed here, so nothing is deleted blind. */
    productCount: z.number().int(),
});
export const createProductCategorySchema = productCategorySchema
    .omit({ id: true, productCount: true })
    .extend({
    description: z.string().trim().max(300).nullable().default(null),
    sortOrder: z.number().int().min(0).max(9999).default(100),
    isActive: z.boolean().default(true),
    allowedType: z.enum(PRODUCT_TYPES).nullable().default(null),
});
export const updateProductCategorySchema = productCategorySchema
    .omit({ id: true, productCount: true })
    .partial();
// ------------------------------------------------------- attribute definitions
/**
 * Reusable suggestions for product attributes (t0919) — e.g. Color: Black,
 * Blue, Red. Suggestions only: a product's attribute value is a copy taken at
 * save time, so the product form still accepts any free-typed value (Gold)
 * that never appears here, and renaming or deleting a definition never
 * touches products already using it.
 */
const attributeChoiceSchema = z.string().trim().min(1).max(100);
export const productAttributeDefinitionSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(64),
    choices: z
        .array(attributeChoiceSchema)
        .max(100)
        .refine((list) => new Set(list.map((c) => c.toLowerCase())).size === list.length, {
        message: 'Choices must be unique',
    }),
    /** Low first, same convention as productCategories.sortOrder. */
    sortOrder: z.number().int().min(0).max(9999),
});
export const createProductAttributeDefinitionSchema = productAttributeDefinitionSchema
    .omit({ id: true })
    .extend({
    choices: productAttributeDefinitionSchema.shape.choices.default([]),
    sortOrder: z.number().int().min(0).max(9999).default(100),
});
export const updateProductAttributeDefinitionSchema = productAttributeDefinitionSchema
    .omit({ id: true })
    .partial();
// ------------------------------------------------- attribute combination prices
/**
 * A full price for one model + attribute combination (t1158) — e.g. a TCL that
 * carries Waze sells for $189 outright, not the normal price plus a surcharge.
 * The attributes use the same shape as a device's own, so a unit matches when
 * it carries every one of them; see `attributePriceFor`.
 */
export const productAttributePriceSchema = z.object({
    attributes: z
        .record(z.string().trim().min(1).max(40), z.union([z.string().trim().min(1).max(120), z.number(), z.literal(true)]))
        .refine((v) => Object.keys(v).length >= 1, 'Choose at least one attribute')
        .refine((v) => Object.keys(v).length <= 10, 'No more than 10 attributes per combination'),
    priceCents: moneyCentsSchema,
});
/** Full replacement of a product's combination prices, like the unit attributes write. */
export const replaceProductAttributePricesSchema = z.object({
    prices: z
        .array(productAttributePriceSchema)
        .max(50)
        .refine((list) => new Set(list.map((p) => JSON.stringify(Object.entries(p.attributes)
        .map(([k, v]) => [k.toLowerCase(), String(v).trim().toLowerCase()])
        .sort(([a], [b]) => String(a).localeCompare(String(b)))))).size === list.length, 'Each combination can only be priced once'),
});
// ------------------------------------------------------------- tax categories
/**
 * A rate products or services can be taxed at (t0947) — a managed list
 * because a shop commonly needs more than one (goods vs labour). Assigned to
 * a product by id, not name: unlike `productCategories`, nothing outside the
 * back office ever reads this by its name, so there is no readable-URL reason
 * to prefer one, and an id survives a rename with nothing to cascade.
 */
export const taxCategorySchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(64),
    /** Basis points: 825 = 8.25%. */
    rateBps: z.number().int().min(0).max(2000),
    /** How many products are filed here, so nothing is deleted blind. */
    productCount: z.number().int(),
});
export const createTaxCategorySchema = taxCategorySchema
    .omit({ id: true, productCount: true })
    .extend({ rateBps: z.number().int().min(0).max(2000).default(0) });
export const updateTaxCategorySchema = taxCategorySchema
    .omit({ id: true, productCount: true })
    .partial();
export const productImageListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
});
/** A product (or variation) currently showing a library image. */
export const productImageUsageSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    sku: z.string(),
    isArchived: z.boolean(),
});
export const productImageRowSchema = z.object({
    id: uuidSchema,
    url: z.url(),
    filename: z.string().nullable(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    usedBy: z.array(productImageUsageSchema),
});
export const libraryDocumentListQuerySchema = productImageListQuerySchema;
/** A PDF / Word file on the Image library's Documents tab (t1389). */
export const libraryDocumentRowSchema = z.object({
    id: uuidSchema,
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    uploadedBy: z.object({ id: uuidSchema, name: z.string() }).nullable(),
});
//# sourceMappingURL=catalog.js.map